import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourStepsRail from '../../components/harbour/HarbourStepsRail';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import { HarbourField, HarbourDateField, dateFieldToIso } from '../../components/harbour/HarbourField';
import { apiHarbourAddPupil } from '../../utils/rmsApi';
import { deriveYearGroup, schoolPhaseOfPupils } from '../../utils/harbourPupils';
import { pilotYearGroups } from '../../constants/pilotEligibility';
import '../../components/harbour/harbour.css';

const STEPS = ["Child or Young Person's Details", 'Home address', 'Parent or carer details'];
const RELATIONSHIPS = [
  { value: 'parent', label: 'Parent' },
  { value: 'step_parent', label: 'Step-parent' },
  { value: 'foster_carer', label: 'Foster carer' },
  { value: 'kinship_carer', label: 'Kinship carer' },
  { value: 'legal_guardian', label: 'Legal guardian' },
];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/*
 * Add a pupil (TDI-53 section 01): a three-step wide flyout. This only adds
 * the pupil to the school's list — pathways are added from the pupil record.
 * Duplicate guard: a pupil with the same name and date of birth already on
 * the list raises a warning with "Open existing record" and "Add anyway"
 * (3192:64190). A date of birth that does not fit the chosen year group is
 * flagged for a second look, never blocked (3895:32660).
 */
export default function HarbourAddPupilFlyout({ orgId, pupils = [], onClose, onAdded }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    firstName: '', lastName: '', dob: {}, yearGroup: '', upn: '',
    street: '', city: '', postcode: '',
    parentFirstName: '', parentLastName: '', relationship: 'parent',
    parentEmail: '', parentPhone: '',
  });
  const [errors, setErrors] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  // Each failed Continue moves focus to the error summary (GOV.UK pattern), so
  // keyboard and screen reader users land on what needs fixing.
  const summaryRef = useRef(null);
  const [summaryNonce, setSummaryNonce] = useState(0);
  useEffect(() => {
    if (summaryNonce && summaryRef.current) summaryRef.current.focus();
  }, [summaryNonce]);
  const [duplicate, setDuplicate] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [busy, setBusy] = useState(false);
  const idempotencyKey = useRef(`add-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  // Correcting a field clears its error as it is typed, the way the SEND
  // wizard already does. Holding the message until the next Continue told a
  // SENCO who had just filled the box in that it was still empty.
  const clearError = (key) => setErrors((prev) => {
    if (!(key in prev)) return prev;
    const rest = { ...prev };
    delete rest[key];
    return rest;
  });
  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    clearError(key);
  };
  const setDob = (dob) => {
    setValues((v) => ({ ...v, dob }));
    clearError('dob');
  };
  const childName = values.firstName.trim() || 'the pupil';
  // Only the year groups the pilot takes, in the school's own phase when its
  // pupils show it.
  const yearGroups = useMemo(() => pilotYearGroups(schoolPhaseOfPupils(pupils)), [pupils]);

  // Duplicate check against the already-loaded list: same name (case
  // insensitive) and same date of birth. The server re-checks on submit.
  const findDuplicate = useMemo(() => {
    const name = `${values.firstName} ${values.lastName}`.trim().toLowerCase();
    const iso = dateFieldToIso(values.dob);
    if (!name || !iso) return null;
    return (
      pupils.find((p) => (
        String(p.name || '').trim().toLowerCase() === name && String(p.dob || '') === iso
      )) || null
    );
  }, [pupils, values.firstName, values.lastName, values.dob]);

  // The year group the date of birth implies, when it disagrees with the one
  // chosen: worth a second look, but some pupils are taught out of year, so it
  // never blocks. A date outside school age has no implied year group and is
  // an error instead, so the warning stays quiet for it.
  const dobYearMismatch = useMemo(() => {
    const iso = dateFieldToIso(values.dob);
    if (!iso || !values.yearGroup) return false;
    const derived = deriveYearGroup(iso);
    return Boolean(derived) && derived !== values.yearGroup;
  }, [values.dob, values.yearGroup]);

  const validateStep = (i) => {
    const errs = {};
    if (i === 0) {
      if (!values.firstName.trim()) errs.firstName = "Enter the pupil's first name";
      if (!values.lastName.trim()) errs.lastName = "Enter the pupil's last name";
      const iso = dateFieldToIso(values.dob);
      const dob = values.dob || {};
      const dobEntered = Boolean(dob.day || dob.month || dob.year);
      // Something typed that is not a date (31 February, a two-digit year)
      // needs a different fix from nothing typed at all.
      if (!iso) errs.dob = dobEntered ? "The pupil's date of birth must be a real date" : "Enter the pupil's date of birth";
      else if (new Date(iso) >= new Date()) errs.dob = "The pupil's date of birth must be in the past";
      // A date that puts the child outside school age is a mistake, not a
      // pupil taught out of year (staging feedback, 30 Oct; wording TDI-801).
      else if (!deriveYearGroup(iso)) errs.dob = 'Enter a date of birth for a child of school age. This pilot is for pupils from Reception to Year 11.';
      if (!values.yearGroup) errs.yearGroup = "Choose the pupil's year group";
    }
    if (i === 2) {
      if (!values.parentFirstName.trim()) errs.parentFirstName = "Enter the parent or carer's first name";
      if (!values.parentLastName.trim()) errs.parentLastName = "Enter the parent or carer's last name";
      if (!values.parentEmail.trim()) errs.parentEmail = "Enter the parent or carer's email address";
      else if (!EMAIL_RE.test(values.parentEmail.trim())) errs.parentEmail = 'Enter an email address in the correct format, like name@example.com';
    }
    setErrors(errs);
    const failed = Object.keys(errs).length > 0;
    setShowSummary(failed);
    if (failed) setSummaryNonce((n) => n + 1);
    return !failed;
  };

  const next = () => {
    if (!validateStep(step)) return;
    if (step === 0 && findDuplicate && !duplicate?.confirmed) {
      setDuplicate({ pupil: findDuplicate, confirmed: false });
      return;
    }
    setDuplicate(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    if (!validateStep(2)) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const res = await apiHarbourAddPupil({
        orgId,
        child: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          dob: dateFieldToIso(values.dob),
          yearGroup: values.yearGroup,
          upn: values.upn.trim() || undefined,
        },
        address: {
          street: values.street.trim() || undefined,
          city: values.city.trim() || undefined,
          postcode: values.postcode.trim() || undefined,
        },
        parent: {
          firstName: values.parentFirstName.trim(),
          lastName: values.parentLastName.trim(),
          email: values.parentEmail.trim(),
          phone: values.parentPhone.trim() || undefined,
          relationship: values.relationship,
        },
        // The design's optional second parent or carer (3192:64186) is not
        // asked for here yet: rmsHarbourAddPupil takes one `parent`, and a
        // guardian participant is keyed on an email address the design
        // deliberately does not collect for them. Collecting those details
        // and dropping them on submit would be worse than not asking, so the
        // block waits on a `secondParent` field on the callable.
        confirmDuplicate: Boolean(duplicate && duplicate.confirmed),
        idempotencyKey: idempotencyKey.current,
      });
      if (onAdded) await onAdded();
      navigate(`/rms/pupils/${res.patientRecordId}`, {
        replace: true,
        state: { harbourAlert: `${values.firstName.trim()} ${values.lastName.trim()} added to Your pupils` },
      });
    } catch (e) {
      if (e && e.code === 'functions/already-exists') {
        const dupId = e.details && e.details.duplicatePatientRecordId;
        setDuplicate({ pupil: pupils.find((p) => p.id === dupId) || { id: dupId, name: `${values.firstName} ${values.lastName}` }, confirmed: false });
        setStep(0);
      } else {
        setSubmitError(e?.message || 'The pupil could not be added. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const footer = (primaryLabel, onPrimary, { showBack = true, leadingIcon, trailingIcon = 'arrow-right', busyLabel } = {}) => (
    <div className="harbour-form-footer">
      <HarbourButton kind="tertiary" onClick={onClose}>Cancel</HarbourButton>
      {showBack && <HarbourButton kind="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))}>Back</HarbourButton>}
      {/* Disabled while it runs: a second click would submit the pupil twice. */}
      <HarbourButton
        kind="primary"
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        busy={busy}
        busyLabel={busyLabel}
        disabled={busy}
        aria-busy={busy}
        onClick={onPrimary}
      >
        {primaryLabel}
      </HarbourButton>
    </div>
  );

  return (
    <HarbourFlyout
      width="wide"
      title="Add pupil"
      titleSize="heading"
      help={step === 2
        ? 'Enter details of those who hold parental responsibility for the child'
        : "Enter the pupil's details as they appear in your school records."}
      onClose={onClose}
    >
      <div className="harbour-task-body">
        <div className="harbour-task-body__steps">
          <HarbourStepsRail tone="senco" steps={STEPS} currentIndex={step} />
        </div>
        <div className="harbour-task-body__main flex flex-col gap-4">
          <div className="harbour-form-card">
            {/* The summary and the duplicate warning open the card, as drawn
                (3192:64190). */}
            {/* Once every field has been corrected there is nothing left
                "marked with an error message", so the summary goes too. */}
            {showSummary && Object.keys(errors).length > 0 && (
              <div ref={summaryRef} tabIndex={-1} className="outline-none">
                <HarbourNotice kind="error" title="Check the details below">
                  Fix the fields marked with an error message, then try again.
                </HarbourNotice>
              </div>
            )}
          {duplicate && !duplicate.confirmed && (
            <HarbourNotice kind="warning" title="Possible duplicate found">
              <p>A pupil with the same name and date of birth is already on your list. Open their record to check before adding another.</p>
              <div className="harbour-card mt-3 p-4 rounded-xl">
                <p className="harbour-body-16-bold">{duplicate.pupil?.name}</p>
                <p className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">
                  {[duplicate.pupil?.yearGroup, 'already on your list'].filter(Boolean).join(' · ')}
                </p>
              </div>
            </HarbourNotice>
          )}
            {step === 0 && (
              <>
                <h2 className="harbour-form-card__title">Child or Young Person&apos;s Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HarbourField label="First name" placeholder="Enter the pupil's first name" value={values.firstName} onChange={set('firstName')} error={errors.firstName} />
                  <HarbourField label="Last name" placeholder="Enter the pupil's last name" value={values.lastName} onChange={set('lastName')} error={errors.lastName} />
                </div>
                <HarbourDateField label="Date of birth" value={values.dob} onChange={setDob} error={errors.dob} />
                <HarbourField as="select" label="Year group" value={values.yearGroup} onChange={set('yearGroup')} error={errors.yearGroup} className="md:max-w-[344px]">
                  <option value="">Choose the pupil&apos;s year group</option>
                  {yearGroups.map((yg) => <option key={yg} value={yg}>{yg}</option>)}
                </HarbourField>
                {dobYearMismatch && (
                  // Hedged, and it says the SENCO may carry on: a pupil taught
                  // outside their usual year group is a real case, not a mistake.
                  <HarbourNotice kind="warning" title="Check the date of birth">
                    {`This date of birth doesn’t usually match ${values.yearGroup}. Please check it’s correct. If the pupil is taught outside their usual year group, you can carry on.`}
                  </HarbourNotice>
                )}
                <HarbourField
                  label="Unique pupil number (optional)"
                  hint="From your school records, if you have it"
                  value={values.upn}
                  onChange={set('upn')}
                  className="md:max-w-[344px]"
                />
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={onClose}>Cancel</HarbourButton>
                  {duplicate && !duplicate.confirmed && (
                    <>
                      <HarbourButton kind="secondary" onClick={() => { setDuplicate((d) => ({ ...d, confirmed: true })); setStep(1); }}>
                        Add anyway
                      </HarbourButton>
                      <HarbourButton kind="primary" onClick={() => navigate(`/rms/pupils/${duplicate.pupil?.id}`)}>
                        Open existing record
                      </HarbourButton>
                    </>
                  )}
                  {!(duplicate && !duplicate.confirmed) && (
                    <HarbourButton kind="primary" trailingIcon="arrow-right" onClick={next}>Continue</HarbourButton>
                  )}
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <h2 className="harbour-form-card__title">Home address</h2>
                <HarbourField label="Street Address" value={values.street} onChange={set('street')} />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-4">
                  <HarbourField label="City or Town" value={values.city} onChange={set('city')} />
                  <HarbourField label="Postcode" value={values.postcode} onChange={set('postcode')} />
                </div>
                {footer('Continue', next)}
              </>
            )}
            {step === 2 && (
              <>
                <h2 className="harbour-form-card__title">Parent or carer details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HarbourField label="First name" value={values.parentFirstName} onChange={set('parentFirstName')} error={errors.parentFirstName} />
                  <HarbourField label="Last name" value={values.parentLastName} onChange={set('parentLastName')} error={errors.parentLastName} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HarbourField as="select" label={`Relationship to ${childName}`} value={values.relationship} onChange={set('relationship')}>
                    {RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </HarbourField>
                  <HarbourField label="Phone number" type="tel" value={values.parentPhone} onChange={set('parentPhone')} />
                </div>
                <HarbourField
                  label="Email address"
                  hint="We send the sign-in email to this address"
                  type="email"
                  value={values.parentEmail}
                  onChange={set('parentEmail')}
                  error={errors.parentEmail}
                />
                {submitError && (
                  <HarbourNotice kind="error" title="The pupil could not be added">{submitError}</HarbourNotice>
                )}
                {footer('Add pupil', submit, { leadingIcon: 'plus', trailingIcon: null, busyLabel: 'Adding' })}
              </>
            )}
          </div>
        </div>
        <aside className="harbour-task-body__aside">
          <HarbourNotice kind="info" title="This only adds the pupil to your list">
            You add them to the SEND pathway or the referral pathway from their pupil record.
          </HarbourNotice>
        </aside>
      </div>
    </HarbourFlyout>
  );
}
